import {autoserializeAs, inheritSerialization} from "dcerialize";
import type {ICustomSortable} from "../base/ICustomSortable";
import type {Existing, IdentifiableModel, IdOf} from "../base/IdentifiableModel";
import {createExisting} from "../base/IdentifiableModel";
import type {ModificationTrackedModel} from "../base/ModificationTrackedModel";
import type {SoftDeletableModel} from "../base/SoftDeletableModel";
import {ModificationDetails} from "../ModificationDetails";

export enum ReportServiceDisplayType {
  Standard = "standard",
  Short = "short",
  Difference = "difference",
  Blind = "blind",
  SpanMass = "spanmass",
  Leakage = "leakage",
}

export class IdOnlyReportService implements IdentifiableModel {
  @autoserializeAs(() => Number)
  public id?: number;

  public static existing(id: IdOf<IdOnlyReportService>): Existing<IdOnlyReportService> {
    return createExisting(IdOnlyReportService, id);
  }
}

@inheritSerialization(() => IdOnlyReportService)
export class SimpleReportService extends IdOnlyReportService implements SoftDeletableModel, ICustomSortable {
  @autoserializeAs(() => String)
  public readonly name: string;
  @autoserializeAs(() => Boolean)
  public readonly isDeleted: boolean;
  @autoserializeAs(() => Boolean)
  public thirdPartyCompanyAllowed: boolean;
  @autoserializeAs(() => Boolean)
  public defectEditor: boolean;
  @autoserializeAs(() => Boolean)
  public showLeakage: boolean;
  @autoserializeAs(() => Number)
  public sortOrder: number;
}

/**
 * @sealed
 */
@inheritSerialization(() => SimpleReportService)
export class ReportService extends SimpleReportService implements ModificationTrackedModel {
  @autoserializeAs(() => String)
  public displayType: ReportServiceDisplayType;
  @autoserializeAs(() => String)
  public markerColorHex: string;
  @autoserializeAs(() => Boolean)
  public useSketch: boolean;
  @autoserializeAs(() => Boolean)
  public showOnlyChecked: boolean;
  @autoserializeAs(() => Boolean)
  public showCip: boolean;
  @autoserializeAs(() => Boolean)
  public showHygiene: boolean;
  @autoserializeAs(() => Boolean)
  public showSpanMass: boolean;

  @autoserializeAs(() => Boolean)
  public readonly showResidueGraph: boolean;
  @autoserializeAs(() => Number)
  public readonly maxDefectReportImageCount: number;
  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;
}

/**
 * @sealed
 */
export class ReportServiceTranslation implements ModificationTrackedModel {
  @autoserializeAs(() => String)
  public name: string;
  @autoserializeAs(() => ModificationDetails)
  public readonly modificationDetails?: ModificationDetails;
}
